import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { Badge, Box, Dialog, IconButton, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DateCalendar, LocalizationProvider, PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import CalenderIcon from "../../../assets/icons/calendar_icon.svg";

const CustomBadge = styled(Badge)(({ invisible }) => ({
  "& .MuiBadge-dot": {
    bottom: "0px",
    left: "50%",
    transform: "translateX(-50%)",
    display: invisible ? "none" : "block",
  },
}));

type CalendarContainerProps = {
  dateChanged: boolean;
  selectedDate: dayjs.Dayjs | null;
  isCalendarVisible: boolean;
  setIsCalendarVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleDateChange: (newDate: Dayjs | null) => void;
  markedDates: string[];
};

const CalendarContainer = ({
  dateChanged,
  selectedDate,
  handleDateChange,
  isCalendarVisible,
  setIsCalendarVisible,
  markedDates,
}: CalendarContainerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [currentMonth, setCurrentMonth] = useState<Dayjs>(selectedDate || dayjs());
  const [baseMonth, setBaseMonth] = useState<Dayjs>(dayjs().startOf("month"));

  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate);
      setBaseMonth(selectedDate.startOf("month"));
    }
  }, [selectedDate]);

  const CustomDay = (props: any) => {
    const { day, outsideCurrentMonth, ...rest } = props;
    const formattedDate = dayjs(day).format("YYYY-MM-DD");

    if (outsideCurrentMonth) return <PickersDay day={day} outsideCurrentMonth {...rest} />;

    // Check if this date has bookings/referrals from markedDates array
    const hasBooking = markedDates.includes(formattedDate);
    const isToday = dayjs().isSame(day, 'day');
    const isSelected = selectedDate && dayjs(selectedDate).isSame(day, 'day');

    return (
      <CustomBadge
        overlap="circular"
        variant="dot"
        invisible={!hasBooking}
        color="success"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <PickersDay
          day={day}
          {...rest}
          sx={{
            // Today's date - black underline (always visible when it's today)
            ...(isToday && {
              borderBottom: "2px solid black",
              fontWeight: 600,
            }),
            // Selected date - green circle
            ...(isSelected && {
              bgcolor: "#4caf50 !important",
              color: "white !important",
              fontWeight: 600,
              borderRadius: "50%",
              "&:hover": {
                bgcolor: "#45a049 !important"
              }
            }),
          }}
        />
      </CustomBadge>
    );
  };

  const handleCalendarOpen = () => setIsCalendarVisible(true);
  const handleCalendarClose = () => setIsCalendarVisible(false);

  const handleDateSelect = (date: Dayjs | null) => {
    if (date) {
      setCurrentMonth(date);
      setBaseMonth(date.startOf("month"));
    }
    handleDateChange(date);
    setIsCalendarVisible(false);
  };

  const handleResetToMostRecent = () => {
    handleDateChange(null); 
    setCurrentMonth(dayjs());
    setBaseMonth(dayjs().startOf("month"));
  };

  const getDisplayText = () => {
    if (dateChanged && selectedDate) {
      return selectedDate.format("MMMM DD");
    }
    return "Most Recent";
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        gap: 2
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: dateChanged ? 'pointer' : 'default'
              }}
              onClick={dateChanged ? handleCalendarOpen : undefined}
            >
              <Typography
                sx={{
                  textDecoration: 'underline',
                  fontSize: '26px',
                  fontWeight: 600,
                }}
              >
                {getDisplayText()}
              </Typography>
              {!dateChanged && <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 34 }} />}
            </Typography>
          </Box>
        </Box>

        {/* Right trigger - open calendar or reset to most recent */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              paddingTop: '2px',
              paddingBottom:'2px',
              paddingLeft:'18px',
              paddingRight:'18px',
              border: '1px solid #ddd',
              borderRadius: '24px',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
            onClick={dateChanged ? handleResetToMostRecent : handleCalendarOpen}
          >
            {!dateChanged && (
              <img src={CalenderIcon} width={15} height={15} alt="calendar-icon" />
            )}
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
              {dateChanged ? "Most Recent" : "Reservation Date"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Dual Calendar Dialog */}
      <Dialog
        open={isCalendarVisible}
        onClose={handleCalendarClose}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            minWidth: { xs: '90vw', sm: '600px', md: '700px' }
          }
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" px={3} pt={3} pb={2}>
          <Typography fontSize={{ xs: "20px", md: "24px" }} fontWeight="600">
            Reservation Date
          </Typography>
          <IconButton onClick={handleCalendarClose} size="small">
            <Typography fontSize="24px" fontWeight="400">×</Typography>
          </IconButton>
        </Box>

        {!isMobile && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 4,
            py: 2
          }}>
            <IconButton
              size="small"
              onClick={() => setBaseMonth((m) => m.subtract(1, "month"))}
              sx={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Box sx={{ display: 'flex', gap: 8 }}>
              <Typography fontWeight={500} fontSize="16px" minWidth="140px" textAlign="center">
                {baseMonth.format("MMMM YYYY")}
              </Typography>
              <Typography fontWeight={500} fontSize="16px" minWidth="140px" textAlign="center">
                {baseMonth.add(1, "month").format("MMMM YYYY")}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={() => setBaseMonth((m) => m.add(1, "month"))}
              sx={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Calendar Container */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 0, md: 4 },
            px: 3,
            pb: 2
          }}
        >
          {/* Left Calendar */}
          <Box borderBottom="1px solid #f2f2f2">
            <DateCalendar
              key={baseMonth.format("YYYY-MM") + "-left"}
              value={null}
              referenceDate={baseMonth}
              onChange={handleDateSelect}
              slots={{ day: CustomDay }}
              sx={{
                "& .MuiPickersCalendarHeader-root": { display: { md: "none", xs: "flex" } },
                "& .MuiPickersArrowSwitcher-root": { display: { md: "none", xs: "block" } },
                "& .MuiDayCalendar-weekDayLabel": {
                  color: "#666",
                  fontWeight: 500,
                  fontSize: "12px"
                },
                "& .MuiPickersDay-root": { fontSize: "14px", fontWeight: 400 },
                maxHeight: "280px"
              }}
            />
          </Box>

          {/* Right Calendar */}
          {!isMobile && (
            <Box borderBottom="1px solid #f2f2f2">
              <DateCalendar
                key={baseMonth.format("YYYY-MM") + "-right"}
                value={null}
                referenceDate={baseMonth.add(1, "month")}
                onChange={handleDateSelect}
                slots={{ day: CustomDay }}
                sx={{
                  "& .MuiPickersCalendarHeader-root": { display: "none" },
                  "& .MuiPickersArrowSwitcher-root": { visibility: "hidden" },
                  "& .MuiDayCalendar-weekDayLabel": {
                    color: "#666",
                    fontWeight: 500,
                    fontSize: "12px"
                  },
                  "& .MuiPickersDay-root": { fontSize: "14px", fontWeight: 400 },
                  maxHeight: "280px"
                }}
              />
            </Box>
          )}
        </Box>

        {/* Done Button */}
        <Box px={3} pb={3}>
          <Box
            onClick={handleCalendarClose}
            sx={{
              bgcolor: '#4caf50',
              color: 'white',
              py: 1.5,
              px: 3,
              borderRadius: '8px',
              textAlign: 'center',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '16px',
              maxWidth: '150px',
              '&:hover': {
                bgcolor: '#45a049'
              }
            }}
          >
            Done
          </Box>
        </Box>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CalendarContainer;